import React, { useState, useEffect, useRef } from 'react';
import MessageBubble from './components/MessageBubble.jsx';
import EscalationButton from './components/EscalationButton.jsx';
import InlineContactForm from './components/InlineContactForm.jsx';
import CannotAnswerContactForm from './components/CannotAnswerContactForm.jsx';
import { trackArchy } from '../lib/archyTelemetry';

export default function ChatApp({
  context = 'default',
  initialMessage = '',
  quickPrompts = [],
  variant = 'default',
  /** Static snapshot (optional). Prefer getContextPayload when data changes during the session. */
  contextPayload = null,
  /** () => object | null — fresh snapshot on each message (e.g. live dashboard). */
  getContextPayload = null,
  /**
   * What the visitor is reading, when it is a single piece: { kind, slug, title, path }.
   * Kept separate from contextPayload on purpose. A non-empty contextPayload
   * disables the public cannot-answer handoff, which is right for ALI screens
   * supplying live data and wrong for "this person is on an essay".
   */
  pageContext = null,
}) {
  const marketing = variant === 'marketing';
  const rh = marketing && context === 'remaining-human';
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showEscalation, setShowEscalation] = useState(false);
  const [conversationState, setConversationState] = useState('greeting');
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [showGreeting, setShowGreeting] = useState(false);
  const [isAbusive, setIsAbusive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSentInitialMessage, setHasSentInitialMessage] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showCannotAnswerForm, setShowCannotAnswerForm] = useState(false);
  const [cannotAnswerQuestion, setCannotAnswerQuestion] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  /** When set, replaces static quickPrompts after an assistant reply (server-generated follow-ups). */
  const [dynamicQuickPrompts, setDynamicQuickPrompts] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const suggestionPrompts =
    dynamicQuickPrompts && dynamicQuickPrompts.length > 0 ? dynamicQuickPrompts : quickPrompts;

  // Scroll only the messages panel — avoid scrollIntoView scrolling the whole page (ALI overlays).
  useEffect(() => {
    requestAnimationFrame(() => {
      const el = messagesContainerRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }, [messages, isLoading]);


  // Show greeting immediately - context-aware (skip if initialMessage provided)
  useEffect(() => {
    if (initialMessage) {
      // Skip greeting if user already has a question
      setMessages([]);
      return;
    }
    
    setShowGreeting(true);
    let greetingText;
    
    if (context === 'home') {
      greetingText = "Hi, I'm Archy. I see you're exploring the homepage. I'm here to help you understand servant leadership, culture building, and how Bart's 34 years of experience can help your business. What would you like to know about leadership, culture, or how we can work together?";
    } else if (context === 'journal') {
      greetingText = "Hi, I'm Archy. I see you're reading the journal. I can help you dive deeper into any of these leadership topics or answer questions about the ideas Bart shares. What would you like to explore?";
    } else if (context === 'methods-mentorship') {
      greetingText = "Hi, I'm Archy. You're looking at Mentorship. I can help you understand how Bart works with leaders one-on-one, what mentorship looks like, and whether this might be a good fit. What would you like to know?";
    } else if (context === 'methods-consulting') {
      greetingText = "Hi, I'm Archy. You're exploring Consulting. I can help answer questions about how Bart works with teams and organizations, what consulting engagements look like, and how this might help your situation. What questions do you have?";
    } else if (context === 'methods-fractional-roles') {
      greetingText = "Hi, I'm Archy. You're exploring Fractional Roles. I can help answer questions about how fractional leadership works, when it's the right fit, and what roles Bart can step into. What questions do you have?";
    } else if (context === 'methods') {
      greetingText = "Hi, I'm Archy. You're exploring Methods—how Bart works with leaders, teams, and organizations. I can help you understand the different approaches, what to expect, and which might be right for you. What would you like to know?";
    } else if (context === 'mentoring') {
      greetingText = "Hi, I'm Archy. You're looking at our mentoring and consulting services. I can help you understand how Bart works with leaders and teams, what to expect, and whether this might be a good fit for you. What questions do you have?";
    } else if (context === 'culture-science') {
      greetingText = "Hi, I'm Archy. You're exploring Culture Science and ALI. I can help explain how we measure culture, what the research shows, and how ALI can help your organization. What would you like to know?";
    } else if (context === 'archy') {
      greetingText = "Hi, I'm Archy. You're learning about me! I'm here to help you understand how I work, what I know, and how I can assist you. Feel free to ask me anything about leadership, culture, or Bart's work.";
    } else if (context === 'philosophy') {
      greetingText = "Hi, I'm Archy. You're exploring Bart's philosophy on leadership. I can help you understand these principles more deeply and how they apply in practice. What would you like to discuss?";
    } else if (context === 'about') {
      greetingText = "Hi, I'm Archy. You're reading about Bart. I can help answer questions about his background, experience, and approach. What would you like to know?";
    } else if (context === 'contact') {
      greetingText = "Hi, I'm Archy. I see you're on the contact page. I'm here to help answer questions about leadership, culture, methods, or how Bart can help your situation. What would you like to know?";
    } else if (context === 'faith') {
      greetingText =
        "Hi, I'm Archy. You're on Faith — devotionals and reflection. I can help you go deeper on what you're reading, suggest how to apply it, or pray through the ideas with you. What would you like to explore?";
    } else if (context === 'engagement-inquiry') {
      greetingText =
        "Hi, I'm Archy. You're looking at an engagement inquiry. I can help you think through fit, timing, or what a next step might look like — or answer questions about how Bart works with leaders. What’s on your mind?";
    } else if (context === 'remaining-human') {
      greetingText =
        "Hi, I'm Archy. You're looking at Remaining Human—Bart's field guide for leading when speed, pressure, and AI-shaped systems stack together. Ask about the ideas in the book, whether it's a fit for you, or how to think clearly when everything accelerates. What would you like to explore?";
    } else if (context === 'advisory') {
      greetingText =
        "Hi, I'm Archy. You're on the Advisory page — Bart's book The Room and the option for private advisory outside your organization. Ask about the book's argument, whether advisory might fit your situation, or what \"outside the room\" means in practice. What would you like to explore?";
    } else if (context === 'the-room') {
      greetingText =
        "Hi, I'm Archy. You're reading about The Room, Bart's book on why no room inside an organization is ever fully honest. Ask about the book's argument, what \"outside the room\" means in practice, or what working with Bart directly looks like. What would you like to explore?";
    } else if (typeof context === 'string' && context.startsWith('ali-')) {
      greetingText =
        "Hi, I'm Archy. I'm here to help you interpret your ALI data and think through practical next steps. What would you like to explore?";
    } else {
      greetingText = "Hi, I'm Archy.\n\nI'm an AI that represents the work, philosophy, and experience of Bart Paden - a builder who's spent more than 34 years creating companies, growing people, and learning what makes both endure. You can ask me just about any question and I'll do my best to speak on his behalf. Go ahead and give it a try.";
    }
    
    const greetingMessage = {
      text: greetingText,
      isUser: false,
      showButtons: false
    };
    setMessages([greetingMessage]);
  }, [context, initialMessage]);

  // Auto-send initial message if provided (from preview input)
  useEffect(() => {
    if (initialMessage && !hasSentInitialMessage) {
      // Wait a moment for component to mount, then send initial message
      const timer = setTimeout(() => {
        setHasSentInitialMessage(true);
        setInputValue(initialMessage);
        // handleSendMessage will add the user message itself, so we don't add it here
        handleSendMessage(initialMessage);
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, hasSentInitialMessage]);

  const detectAbuse = (message) => {
    const messageLower = message.toLowerCase();
    
    // Context-aware abuse detection - only flag actual threats/insults, not casual language
    // Words that are ONLY abusive when directed at someone or used as threats
    const directedInsults = [
      /(you're|you are|you)\s+(an?\s+)?(idiot|stupid|dumb|worthless|useless|asshole|bitch)/i,
      /(fuck|shit)\s+(you|off|yourself)/i,
      /(go|get)\s+(fuck|die)/i
    ];
    
    // Direct threats
    const threats = [
      /(kill|die|hurt|harm)\s+(you|yourself|your|me|myself)/i,
      /(hope|wish)\s+(you|they|he|she)\s+(die|kill|hurt)/i
    ];
    
    // Check for directed insults or threats
    const hasDirectedInsult = directedInsults.some(pattern => pattern.test(message));
    const hasThreat = threats.some(pattern => pattern.test(message));
    
    // Only flag if it's actually abusive (directed at someone or threatening)
    // Allow casual language like "damn, that's awesome" or "this is shit" (describing a situation)
    return hasDirectedInsult || hasThreat;
  };

  const detectDisinterest = (message) => {
    const disinterestKeywords = ['not interested', 'don\'t want', 'no thanks', 'not for me', 'pass', 'skip', 'boring', 'waste of time'];
    return disinterestKeywords.some(keyword => message.toLowerCase().includes(keyword));
  };

  const detectRelevantTopics = (message) => {
    const relevantKeywords = ['building', 'company', 'business', 'leadership', 'team', 'management', 'startup', 'growth', 'strategy', 'culture', 'clarity', 'mentor', 'consulting', 'help', 'advice', 'guidance'];
    return relevantKeywords.some(keyword => message.toLowerCase().includes(keyword));
  };

  const detectJourneyStage = (message) => {
    const messageLower = message.toLowerCase();
    
    // Check for specific journey indicators
    if (messageLower.includes('stepping into leadership') || 
        messageLower.includes('new leader') || 
        messageLower.includes('emerging leader') ||
        messageLower.includes('first leadership role')) {
      return 'leading';
    }
    
    if (messageLower.includes('building') || 
        messageLower.includes('leading a company') || 
        messageLower.includes('founder') ||
        messageLower.includes('ceo') ||
        messageLower.includes('executive')) {
      return 'building';
    }
    
    if (messageLower.includes('clarity') || 
        messageLower.includes('transition') || 
        messageLower.includes('purpose') ||
        messageLower.includes('direction') ||
        messageLower.includes('lost')) {
      return 'clarity';
    }
    
    if (messageLower.includes('learn about bart') || 
        messageLower.includes('who is bart') || 
        messageLower.includes('tell me about bart')) {
      return 'learn';
    }
    
    return null;
  };

  const handleSendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading || isBlocked) return;

    // Counted here rather than from archy_questions, because that table only
    // records questions the server finished answering. A question that errored,
    // timed out, or was blocked still tells us someone tried, and those are the
    // ones worth knowing about.
    trackArchy('asked', { context, turn: messages.filter((m) => m.isUser).length + 1 });

    const userMessage = { text: messageText, isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Check for obvious abuse patterns first (client-side quick check)
    // But don't block - let server AI assess the real threat level
    const hasObviousAbuse = detectAbuse(messageText);
    
    if (hasObviousAbuse) {
      // Still send to server, but also assess with AI on server side
      // Server will determine if it's a real threat or just casual language
    }

    // Check for disinterest
    if (detectDisinterest(messageText)) {
      const analogMessage = {
        text: "I get it. This AI stuff freaks us out too.",
        isUser: false,
        showButtons: true,
        buttonOptions: [
          { text: "Go Analog", value: "go_analog" }
        ]
      };
      setMessages(prev => [...prev, analogMessage]);
      setIsLoading(false);
      return;
    }

    // Always go to AI conversation - no structured paths
    // Let the AI handle everything naturally

    // Default to AI conversation for other topics
    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text
      }));

      // First, assess threat with AI if message seems problematic
      if (hasObviousAbuse) {
        try {
          const threatAssessment = await fetch('/api/chat/assess-threat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              message: messageText,
              conversationHistory
            })
          });

          const assessmentData = await threatAssessment.json();
          
          if (assessmentData.blocked) {
            setIsBlocked(true);
            setIsLoading(false);
            
            let blockMessage;
            if (assessmentData.blockType === 'permanent') {
              blockMessage = {
                text: "This conversation has been terminated due to threatening behavior. The chat is now permanently closed.",
                isUser: false,
                showButtons: false
              };
            } else {
              // Temporary 24-hour block
              const expiresAt = new Date(assessmentData.expiresAt);
              const hoursRemaining = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60));
              blockMessage = {
                text: `Due to repeated inappropriate behavior, this chat has been temporarily suspended. You can try again in ${hoursRemaining} hours.`,
                isUser: false,
                showButtons: false
              };
            }
            
            setMessages(prev => [...prev, blockMessage]);
            return;
          }
        } catch (err) {
          console.error('Error assessing threat:', err);
          // Continue to normal chat flow if assessment fails
        }
      }

      const snap =
        typeof getContextPayload === 'function' ? getContextPayload() : contextPayload;
      const payload =
        snap && typeof snap === 'object' && !Array.isArray(snap) && Object.keys(snap).length > 0
          ? { contextPayload: snap }
          : {};

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: messageText,
          conversationHistory,
          sessionId,
          context: context || 'default',
          ...(pageContext ? { pageContext } : {}),
          ...payload,
        }),
      });

      // Check if session is blocked
      if (response.status === 403) {
        const data = await response.json();
        if (data.blocked) {
          setIsBlocked(true);
          let blockMessage;
          if (data.expiresAt) {
            const expiresAt = new Date(data.expiresAt);
            const hoursRemaining = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60));
            blockMessage = {
              text: `This session has been temporarily blocked. You can try again in ${hoursRemaining} hours.`,
              isUser: false,
              showButtons: false
            };
          } else {
            blockMessage = {
              text: "This session has been permanently blocked due to inappropriate behavior. The chat is now closed.",
              isUser: false,
              showButtons: false
            };
          }
          setMessages(prev => [...prev, blockMessage]);
          setIsLoading(false);
          return;
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (Array.isArray(data.followUpPrompts) && data.followUpPrompts.length > 0) {
        setDynamicQuickPrompts(
          data.followUpPrompts.map((t) => ({
            label: String(t).slice(0, 120),
            send: String(t).slice(0, 120),
          }))
        );
      }
      const assistantMessage = { 
        text: data.response, 
        isUser: false,
        showButtons: data.suggestedButtons ? true : false,
        buttonOptions: data.suggestedButtons || undefined
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Handle cannot answer scenario
      if (data.cannotAnswer) {
        setCannotAnswerQuestion(messageText);
        setShowCannotAnswerForm(true);
      }

      if (data.shouldEscalate) {
        // Empathetic prompt with two clear options
        const escalatePrompt = {
          text: "Looks like you may need to talk with Bart directly. We can do that. Here are two options:",
          isUser: false,
          showButtons: true,
          buttonOptions: [
            { text: 'Send an Email', value: 'handoff_email' },
            { text: 'Schedule a 1-on-1 meeting', value: 'handoff_schedule' }
          ]
        };
        setMessages(prev => [...prev, escalatePrompt]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.message, error.stack);
      const errorMessage = {
        text: 'Sorry, I encountered an error. Please try again.',
        isUser: false
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (value) => {
    if (value === 'go_analog') {
      // Go Analog - scroll to traditional site sections
      const userMessage = { text: "Go Analog", isUser: true };
      setMessages(prev => [...prev, userMessage]);
      
      const response = {
        text: "Perfect! I'll scroll you down to explore the traditional site structure. You can always come back here to chat with me anytime.",
        isUser: false,
        showButtons: false
      };
      setMessages(prev => [...prev, response]);
      
      // Scroll to About section
      setTimeout(() => {
        const aboutSection = document.getElementById('about');
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 1000);
    } else if (value === 'contact_direct') {
      // Contact Bart directly
      window.open('https://calendly.com/bartpaden/a-conversation-with-bart', '_blank');
    } else if (value === 'handoff_email') {
      // Start the handoff triage/contact flow in-app
      setShowEscalation(true);
    } else if (value === 'show_contact_form') {
      // Show inline contact form
      setShowContactForm(true);
      const response = {
        text: "I can't share Bart's email directly, but you can reach him through this contact form. He checks these messages regularly and will respond to you.",
        isUser: false,
        showButtons: false,
        showContactForm: true
      };
      setMessages(prev => [...prev, response]);
    } else if (value === 'handoff_schedule') {
      // Open Bart's Calendly directly (provided link)
      window.open('https://calendly.com/bartpaden/a-conversation-with-bart', '_blank');
    } else if (value.startsWith('calendly_')) {
      // Handle Calendly links
      const calendlyUrl = process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/bartpaden/a-conversation-with-bart';
      window.open(calendlyUrl, '_blank');
      
      const response = {
        text: "I've opened Bart's calendar for you. Once you've scheduled, we can continue the conversation here.",
        isUser: false,
        showButtons: false
      };
      setMessages(prev => [...prev, response]);
    } else if (value === 'mentorship') {
      // Handle mentorship guidance
      const userMessage = { text: "I want mentorship guidance", isUser: true };
      setMessages(prev => [...prev, userMessage]);
      
      const response = {
        text: "Perfect! Bart's mentorship focuses on practical leadership development. He helps emerging leaders build clarity, confidence, and sustainable habits through his five fundamentals framework.\n\nHis approach is hands-on and based on 34 years of real experience. Conversations run up to ninety minutes, and the first one is free.\n\nWhat specific leadership challenge would you like to work on?",
        isUser: false,
        showButtons: true,
        buttonOptions: [
          { text: "Book a conversation", value: "calendly_schedule" },
          { text: "Learn the five fundamentals", value: "fundamentals" }
        ]
      };
      setMessages(prev => [...prev, response]);
    } else if (value === 'fundamentals') {
      // Handle five fundamentals explanation
      const userMessage = { text: "Learn the five fundamentals", isUser: true };
      setMessages(prev => [...prev, userMessage]);
      
      const response = {
        text: "Here are Bart's five leadership fundamentals:\n\n1. **Clarity beats chaos** - People can't follow what they can't see. Clear direction, clear expectations, clear outcomes.\n\n2. **Protect the culture** - Values before convenience. When pressure hits, culture is what holds.\n\n3. **Build trust daily** - It's math, not magic. Small consistent actions compound into unshakeable trust.\n\n4. **Empower over control** - Ownership outlasts oversight. Give people the tools and space to succeed.\n\n5. **Serve the standard** - People rise to what you model. Your behavior sets the bar.\n\nThese aren't theory - they're battle-tested principles from 34 years of building teams and companies. Which one resonates most with your current situation?",
        isUser: false,
        showButtons: true,
        buttonOptions: [
          { text: "Book a conversation", value: "calendly_schedule" },
          { text: "Tell me more about my situation", value: "mentorship" }
        ]
      };
      setMessages(prev => [...prev, response]);
    } else if (value === 'handoff') {
      // Handle live handoff request
      const userMessage = { text: "Request live handoff", isUser: true };
      setMessages(prev => [...prev, userMessage]);
      
      const response = {
        text: "Perfect! I'll set up a live handoff to Bart. He'll get a brief of our conversation and can jump in to help you directly.\n\nThis will send him a summary of what we've discussed so he can provide more personalized guidance.",
        isUser: false,
        showButtons: false
      };
      setMessages(prev => [...prev, response]);
      
      // Trigger escalation
      setShowEscalation(true);
    } else {
      // Handle structured conversation paths
      handleStructuredResponse(value);
    }
  };

  const handleStructuredResponse = (value) => {
    const userMessage = { text: getPathText(value), isUser: true };
    setMessages(prev => [...prev, userMessage]);

    let response;
    let nextState;

    switch (value) {
      case 'building':
        response = {
          text: "That's where Bart spent most of his life — leading creative teams, building software companies, and helping organizations grow without losing their soul.\n\nHe now consults founders and operators who need structure, alignment, and systems that hold when things get hard.",
          isUser: false,
          showButtons: true,
          buttonOptions: [
            { text: "Show me how business consulting works", value: "consulting_how" },
            { text: "I'd like to schedule a call", value: "schedule_business" }
          ]
        };
        nextState = 'building_options';
        break;

      case 'leading':
        response = {
          text: "Every great leader starts right here — capable, curious, and looking for direction.\n\nBart mentors emerging leaders, helping them build clarity, confidence, and the habits that make leadership sustainable.\nNo jargon. No theory. Just the logic of how leadership actually works.",
          isUser: false,
          showButtons: true,
          buttonOptions: [
            { text: "Learn the principles", value: "principles" },
            { text: "Schedule mentorship", value: "schedule_mentorship" }
          ]
        };
        nextState = 'leading_options';
        break;

      case 'clarity':
        response = {
          text: "Sometimes growth isn't about fixing a company — it's about finding your footing again.\n\nBart works with individuals who want to rediscover purpose, rebuild confidence, and make better decisions after big transitions.",
          isUser: false,
          showButtons: true,
          buttonOptions: [
            { text: "Learn about clarity mentoring", value: "clarity_how" },
            { text: "Schedule a clarity session", value: "schedule_clarity" }
          ]
        };
        nextState = 'clarity_options';
        break;

      case 'learn':
        response = {
          text: "Bart Paden is a lifelong builder — designer turned entrepreneur, founder turned mentor.\nHe's led creative and technical teams, built companies from nothing, and helped hundreds of people grow along the way.\n\nToday he channels that experience into Archetype Original, helping others build what lasts — businesses, teams, and lives with structure and soul.",
          isUser: false,
          showButtons: true,
          buttonOptions: [
            { text: "Read more of his story", value: "story" },
            { text: "See how he helps", value: "help" }
          ]
        };
        nextState = 'learn_options';
        break;

      default:
        response = {
          text: "That doesn't fit neatly into one of my usual paths, and that's okay. Tell me more about what's happening, and I'll find the best way to help.",
          isUser: false,
          showButtons: false
        };
        nextState = 'freeform';
    }

    setMessages(prev => [...prev, response]);
    setConversationState(nextState);
  };

  const getPathText = (path) => {
    const pathTexts = {
      'building': "I'm building or leading a company",
      'leading': "I'm stepping into leadership",
      'clarity': "I want personal or professional clarity",
      'learn': "I just want to learn more about Bart"
    };
    return pathTexts[path] || path;
  };

  const handleEscalate = async (triageAnswers, conversationHistory) => {
    try {
      const response = await fetch('/api/handoff', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: 'User requested live handoff',
          timestamp: new Date().toISOString(),
          conversationHistory,
          triageAnswers,
          sessionId
        }),
      });

      const data = await response.json();

      const blocks = [];
      blocks.push({ text: data.message || 'Your handoff request has been submitted. We\'ll be in touch soon!', isUser: false });
      if (data.calendlyUrl) {
        blocks.push({
          text: 'You can also schedule time with Bart now:',
          isUser: false,
          showButtons: true,
          buttonOptions: [
            { text: 'Schedule a 1-on-1 meeting', value: 'handoff_schedule' }
          ]
        });
      }

      setMessages(prev => [...prev, ...blocks]);
      setShowEscalation(false);
    } catch (error) {
      console.error('Error submitting handoff:', error);
      const errorMessage = {
        text: 'Sorry, there was an error submitting your handoff request. Please try again.',
        isUser: false
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleSendMessage();
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoading && !isBlocked && inputValue.trim()) {
      handleSendMessage();
    }
    return false;
  };

  const drawerVariant = marketing ? (rh ? 'remaining-human' : 'marketing') : 'default';
  const assistantAvatarSrc =
    marketing && context === 'remaining-human'
      ? '/images/remaining-human/archy-floating-neo.png'
      : undefined;

  return (
    <div
      className={`relative flex h-full min-h-0 flex-col chat-container ${
        marketing ? (rh ? 'bg-[#061312]' : 'bg-[#FAFAF9]') : 'bg-white'
      }`}
    >
      <div
        className={`mx-auto flex h-full min-h-0 w-full flex-1 flex-col ${marketing ? 'px-0' : 'px-3 sm:px-4 md:px-6'}`}
      >
        {suggestionPrompts.length > 0 && (
          <div
            className={
              rh
                ? 'flex-shrink-0 border-b border-[rgba(149,218,206,0.12)] bg-[#081917] px-5 py-3.5'
                : marketing
                  ? 'flex-shrink-0 border-b border-[rgba(26,26,26,0.06)] bg-[#FAFAF9] px-5 py-3.5'
                  : 'flex-shrink-0 border-b border-gray-100 bg-white pb-3 pt-3'
            }
          >
            <p
              className={
                rh
                  ? 'mb-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#A9D8D0]/90'
                  : marketing
                    ? 'mb-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ao-midGray'
                    : 'mb-2 px-1 text-[11px] font-medium uppercase tracking-[0.12em] text-gray-400'
              }
            >
              Suggestions
            </p>
            <div
              className={
                marketing ? 'flex flex-wrap gap-1.5' : 'flex gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
              }
            >
              {suggestionPrompts.map((p, idx) => (
                <button
                  key={`${p.label}-${idx}`}
                  type="button"
                  onClick={() => {
                    const text = p.send ?? p.label;
                    if (!isLoading && !isBlocked && text.trim()) {
                      // Which suggested prompts get used, and whether any do.
                      // The prompts are identical on every page today, so this
                      // is the evidence for whether they are worth tailoring.
                      trackArchy('prompt_clicked', { context, label: p.label });
                      handleSendMessage(text.trim());
                    }
                  }}
                  disabled={isLoading || isBlocked}
                  className={`flex-shrink-0 border px-3 py-1.5 text-left text-xs font-medium transition disabled:opacity-50 ${
                    rh
                      ? 'rounded-[2px] border-[#9ADBD2]/35 bg-[#061312] text-[#E7F1EE] hover:border-[#9ADBD2]/55 hover:bg-[#0A2422]'
                      : marketing
                        ? 'rounded-[2px] border-[rgba(26,26,26,0.14)] bg-white text-[#1A1A1A] hover:border-[rgba(26,26,26,0.22)] hover:bg-ao-cream'
                        : 'rounded-full border-gray-200 bg-white py-2 text-sm text-gray-800 shadow-sm hover:border-[#DB0812]/40 hover:bg-[#fafaf9]'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Messages — flex-1 scrolls; no fixed pixel max height (breaks on short mobile viewports) */}
        <div
          ref={messagesContainerRef}
          className={`min-h-0 flex-1 overscroll-contain overflow-y-auto ${marketing ? `px-5 py-5 archy-drawer-scroll ${rh ? 'bg-[#061312]' : 'bg-white'}` : ''}`}
        >
          {messages.length > 0 && (
            <div className={marketing ? '' : 'py-4 md:py-8'}>
              {messages.map((message, index) => (
                <MessageBubble
                  key={index}
                  message={message.text}
                  isUser={message.isUser}
                  showButtons={message.showButtons}
                  buttonOptions={message.buttonOptions}
                  onButtonClick={handleButtonClick}
                  drawerVariant={drawerVariant}
                  assistantAvatarSrc={assistantAvatarSrc}
                />
              ))}
              {/* Loading indicator */}
              {isLoading && (
                <div className={`flex justify-start ${marketing ? 'mb-4' : 'mb-4'}`}>
                  <div
                    className={`max-w-xs rounded-[2px] border px-4 py-3 ${
                      rh
                        ? 'border-[#95DACE]/20 bg-[#0F2E2C]'
                        : marketing
                          ? 'border-[rgba(26,26,26,0.08)] bg-white'
                          : 'border-warm-border bg-white shadow-sm rounded-lg'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div
                          className={`h-2 w-2 animate-bounce rounded-full ${rh ? 'bg-[#8EE4D8]' : 'bg-ao-red'}`}
                        ></div>
                        <div
                          className={`h-2 w-2 animate-bounce rounded-full ${rh ? 'bg-[#8EE4D8]' : 'bg-ao-red'}`}
                          style={{ animationDelay: '0.1s' }}
                        ></div>
                        <div
                          className={`h-2 w-2 animate-bounce rounded-full ${rh ? 'bg-[#8EE4D8]' : 'bg-ao-red'}`}
                          style={{ animationDelay: '0.2s' }}
                        ></div>
                      </div>
                      <span className={`text-sm ${rh ? 'text-[#C8E8E2]' : marketing ? 'text-[#6B6B6B]' : 'text-warm-grey'}`}>
                        Archy is thinking...
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {/* Scroll anchor */}
              <div ref={messagesEndRef} />
              {/* Cannot Answer Contact Form */}
              {showCannotAnswerForm && cannotAnswerQuestion && (
                <div className="px-4 pb-4">
                  <CannotAnswerContactForm
                    question={cannotAnswerQuestion}
                    onSuccess={() => {
                      setShowCannotAnswerForm(false);
                      setCannotAnswerQuestion(null);
                    }}
                    onSkip={() => {
                      // Email was already sent when cannotAnswer was detected in chat.js
                      // Just close the form and allow chat to continue
                      setShowCannotAnswerForm(false);
                      setCannotAnswerQuestion(null);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input Area - Fixed at bottom; safe area for home indicator on iOS */}
        <div
          className={`w-full flex-shrink-0 border-t pb-[max(1rem,env(safe-area-inset-bottom))] pt-3.5 ${
            marketing ? `px-5 ${rh ? 'border-[rgba(149,218,206,0.15)] bg-[#0A2422]' : 'border-[rgba(26,26,26,0.08)] bg-white'}` : 'border-gray-200 bg-white px-4'
          }`}
        >
          {showEscalation && (
            <EscalationButton 
              onEscalate={handleEscalate} 
              conversationHistory={messages.map(msg => ({
                role: msg.isUser ? 'user' : 'assistant',
                content: msg.text
              }))}
            />
          )}

          <form 
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isLoading && !isBlocked && inputValue.trim()) {
                handleSendMessage();
              }
              return false;
            }} 
            className={`flex items-center gap-2 ${marketing ? '' : 'space-x-2 sm:space-x-3'}`}
            noValidate
          >
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isBlocked ? "Chat is closed" : (isLoading ? "Archy is thinking..." : "Tell me what's going on.")}
              disabled={isLoading || isBlocked}
              className={`flex-1 transition-all duration-300 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
                marketing
                  ? rh
                    ? 'h-10 rounded-[2px] border border-[#9ADBD2]/30 bg-[#061312] px-[14px] text-[13px] font-inter text-[#E7F1EE] placeholder:text-[#A9D8D0]/75 focus:border-[#9ADBD2]/55'
                    : 'h-10 rounded-[2px] border border-[rgba(26,26,26,0.12)] bg-[#FAFAF9] px-[14px] text-[13px] font-inter text-[#1A1A1A] placeholder:text-ao-midGray focus:border-[rgba(26,26,26,0.28)]'
                  : 'rounded-xl border px-4 py-3 text-base border-gray-300 bg-[#E8D5C4]/30 text-[#2B2D2F] placeholder-[#6B6B6B] focus:border-[#DB0812] focus:ring-2 focus:ring-[#DB0812]/20'
              }`}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isLoading && !isBlocked && inputValue.trim()) {
                  handleSendMessage();
                }
              }}
              disabled={!inputValue.trim() || isLoading || isBlocked}
              className={`h-10 shrink-0 whitespace-nowrap px-5 text-xs font-semibold uppercase tracking-[0.06em] transition disabled:cursor-not-allowed disabled:opacity-50 ${
                marketing
                  ? rh
                    ? 'rounded-[2px] bg-[#8EE4D8] text-[#03211F] hover:bg-[#A4ECE2] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8EE4D8] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A2422]'
                    : 'rounded-[2px] bg-ao-red px-5 text-white hover:opacity-[0.88] focus:outline-none focus-visible:ring-2 focus-visible:ring-ao-red focus-visible:ring-offset-2'
                  : 'min-h-[44px] rounded-xl bg-ao-red px-6 py-3 text-base text-white hover:bg-[#b30610] focus:outline-none focus:ring-2 focus:ring-ao-red focus:ring-offset-2'
              }`}
              aria-label="Send message"
            >
              {isLoading ? '...' : 'Send'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}